// J6 (contract 1.4.0 §19–§20, F-J6-6): where to go back to after signing in. The Denwen tray links to
// <portal origin>/portal/tickets/<id>; a signed-out end user must land on that ticket after signing
// in, and nothing else may be smuggled in: only a same-origin relative path is ever followed (never
// //host, a scheme, a backslash, whitespace or control characters), and the portal only returns to
// a path under /portal.

const PROBE_ORIGIN = 'https://return.invalid';

/** `value` when it is a same-origin relative path (under `prefix`, if given), normalised; else null. */
export function safeReturnPath(value: unknown, prefix = '/'): string | null {
  if (typeof value !== 'string' || value.length === 0 || value.length > 2048) return null;
  if (!value.startsWith('/') || value.startsWith('//') || value.includes('\\')) return null;
  // No whitespace or control characters (code points up to space, and DEL).
  if ([...value].some((c) => c.charCodeAt(0) <= 0x20 || c.charCodeAt(0) === 0x7f)) return null;
  let url: URL;
  try {
    url = new URL(value, PROBE_ORIGIN);
  } catch {
    return null;
  }
  if (url.origin !== PROBE_ORIGIN) return null;
  if (prefix !== '/' && url.pathname !== prefix && !url.pathname.startsWith(`${prefix}/`)) return null;
  return `${url.pathname}${url.search}${url.hash}`;
}

// A sign-in through single sign-on leaves the page, so the portal path also waits in this tab's
// sessionStorage until the portal is reached signed in. Storage may be unavailable: then only the
// password sign-in (router state) returns to the ticket.
const KEY = 'denwen-msp.portal.returnTo';

export function rememberPortalReturn(path: string) {
  const safe = safeReturnPath(path, '/portal');
  if (!safe || safe === '/portal' || safe.startsWith('/portal/login')) return;
  try {
    sessionStorage.setItem(KEY, safe);
  } catch {
    /* storage unavailable */
  }
}

/** The remembered portal path, or null. */
export function peekPortalReturn(): string | null {
  try {
    return safeReturnPath(sessionStorage.getItem(KEY), '/portal');
  } catch {
    return null;
  }
}

export function forgetPortalReturn() {
  try {
    sessionStorage.removeItem(KEY);
  } catch {
    /* storage unavailable */
  }
}

/** The remembered portal path (once: it is forgotten as it is read), or null. */
export function takePortalReturn(): string | null {
  const v = peekPortalReturn();
  forgetPortalReturn();
  return v;
}
